/**
 * Theme Redux Slice
 *
 * Stores the active client theme name.
 * Dispatch `setClientTheme('red')` at login/app-config load
 * when the server returns a client configuration.
 *
 * Usage:
 *   import { setClientTheme } from 'src/shared/store/themeSlice';
 *   dispatch(setClientTheme('red'));
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ClientThemeId } from '../theme/clientThemes';

interface ThemeState {
  /** Currently active client theme */
  themeName: ClientThemeId;
}

const initialState: ThemeState = {
  themeName: 'blue',
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    /**
     * Switch to a named client theme.
     * All screens that use `useScreenTheme` will re-render with the new brand colors.
     */
    setClientTheme(state, action: PayloadAction<ClientThemeId>) {
      state.themeName = action.payload;
    },

    /** Reset back to the default blue theme */
    resetTheme(state) {
      state.themeName = 'blue';
    },
  },
});

export const { setClientTheme, resetTheme } = themeSlice.actions;
export default themeSlice.reducer;